import * as React from "react";

import { StaticVariableTag, getFilenameFromUrl, getVariableHtml } from "@simplyk/common";
import { useForm } from "react-hook-form";

import { useTranslate } from "@/common/hooks/useTranslate";
import { EmailEditorFormData } from "@/common/types/email";

type UseEmailEditorFormProps = {
  emailContent?: Partial<EmailEditorFormData>;
  isThankYouEmail?: boolean;
};

export const useEmailEditorForm = ({ emailContent, isThankYouEmail }: UseEmailEditorFormProps) => {
  const { t } = useTranslate();
  const defaultEmailMessage = React.useMemo(() => {
    const firstNameVariableHtml = getVariableHtml({
      text: t("dashboard", "common.firstName"),
      tag: StaticVariableTag.FirstName,
    });
    return `${t("common", "defaultEmailMessage1")}${firstNameVariableHtml}${t("common", "defaultEmailMessage2")}`;
  }, [t]);

  // For thank you emails, don't use default message - let AI generate it
  const formattedDefaultBodyWithStyles = emailContent?.body || (isThankYouEmail ? "" : defaultEmailMessage);

  const defaultAttachment = emailContent?.attachment;
  const formDefaultValues: EmailEditorFormData = {
    subject: emailContent?.subject || "",
    body: formattedDefaultBodyWithStyles,
    recipientEmails: emailContent?.recipientEmails || [],
    attachment: {
      filename:
        defaultAttachment?.filename ||
        (defaultAttachment?.fileUrl ? getFilenameFromUrl(decodeURIComponent(defaultAttachment.fileUrl)) : ""),
      fileUrl: defaultAttachment?.fileUrl || "",
    },
  };

  const form = useForm<EmailEditorFormData>({
    defaultValues: formDefaultValues,
    mode: "onChange",
    shouldUnregister: true,
  });

  const handleBodyChange = (newBody: string) => {
    form.setValue("body", newBody);
  };

  return { form, formDefaultValues, handleBodyChange };
};
